import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Compass } from "lucide-react";
import { useDiagnostic } from "@/context/DiagnosticContext";
import { isComplete } from "@/lib/scoring";
import { saveDiagnostic } from "@/lib/api";
import { pushHistory } from "@/lib/storage";

const MESSAGES = [
  "Analisando os resultados…",
  "Identificando oportunidades…",
  "Preparando seu diagnóstico…",
];

export default function Processing() {
  const navigate = useNavigate();
  const { company, answers, setResult } = useDiagnostic();
  const [msgIdx, setMsgIdx] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isComplete(answers)) {
      navigate("/diagnostico");
      return;
    }

    const rotator = setInterval(() => {
      setMsgIdx((i) => (i + 1) % MESSAGES.length);
    }, 900);

    let cancelled = false;
    const run = async () => {
      const started = Date.now();
      try {
        const data = await saveDiagnostic({ company, answers });
        const elapsed = Date.now() - started;
        const wait = Math.max(0, 3000 - elapsed);
        setTimeout(() => {
          if (cancelled) return;
          setResult(data);
          pushHistory({
            id: data.id,
            empresa: company.empresa,
            total: data.total_score,
            badge: data.badge,
            created_at: data.created_at,
          });
          navigate(`/resultado/${data.id}`);
        }, wait);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError("Não foi possível processar o diagnóstico. Tente novamente.");
      }
    };
    run();

    return () => {
      cancelled = true;
      clearInterval(rotator);
    };
  }, []);

  return (
    <div
      data-testid="processing-page"
      className="min-h-[80vh] grid place-items-center px-6"
    >
      <div className="text-center max-w-lg">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-[#0F172A] text-white grid place-items-center hub-pulse">
          <Compass size={26} strokeWidth={2} />
        </div>
        <div className="mt-8 hub-eyebrow">HUB · Processando</div>
        <h1 className="mt-3 font-display font-medium text-3xl tracking-tight">
          Gerando seu diagnóstico executivo
        </h1>
        <div className="mt-8 h-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={msgIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="text-slate-600"
              data-testid="processing-message"
            >
              {MESSAGES[msgIdx]}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="mt-8 mx-auto w-64 h-1 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: "10%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 2.8, ease: "easeInOut" }}
            className="h-full bg-[#D97706]"
          />
        </div>

        {error && (
          <div
            data-testid="processing-error"
            className="mt-8 text-sm text-[#DC2626]"
          >
            {error}{" "}
            <button
              className="underline"
              onClick={() => navigate("/diagnostico")}
            >
              Voltar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
